import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, from, EMPTY, switchMap, takeUntil, timer } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LIVECHAT_VERSION } from 'src/app/shared/utils/livechat_version';
import { LoggerService } from './logger.service';
import { LogEvent } from '../enums/log-event';
import { TranslateService } from '@ngx-translate/core';

interface VersionResponse {
  version: string;
  message?: Record<string, string>;
}

const POLLING_INTERVAL_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 6000;
const DISMISS_COOLDOWN_MS = 60 * 60 * 1000;
const DISMISS_STORAGE_KEY = 'livechat_version_update_dismissed_at';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService implements OnDestroy {
  private readonly updateAvailableSub = new BehaviorSubject<boolean>(false);
  public readonly updateAvailable$ = this.updateAvailableSub.asObservable();

  private readonly releaseMessageSub = new BehaviorSubject<string>('');
  public readonly releaseMessage$ = this.releaseMessageSub.asObservable();

  private readonly destroy$ = new Subject<void>();

  private latestRemoteVersion: string | null = null;

  constructor(
    @Inject(LIVECHAT_VERSION) private currentVersion: string,
    private loggerService: LoggerService,
    private ngZone: NgZone,
    private translateService: TranslateService
  ) {}

  public startPolling(): void {
    if (!environment.app.production) {
      return;
    }

    timer(0, POLLING_INTERVAL_MS)
      .pipe(
        switchMap(() => this.checkVersion()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  public dismiss(): void {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    this.updateAvailableSub.next(false);
  }

  public applyUpdate(): void {
    this.loggerService.log(LogEvent.LIVECHAT_VERSION_UPDATE_CLICKED, {
      currentVersion: this.currentVersion,
      targetVersion: this.latestRemoteVersion || 'unknown',
    });
    window.location.reload();
  }

  private checkVersion() {
    const cacheBust = `?t=${Date.now()}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    const fetchPromise = fetch(`assets/version.json${cacheBust}`, { signal: controller.signal })
      .then((res) => {
        clearTimeout(timeoutId);
        if (!res.ok) return null;
        return res.json() as Promise<VersionResponse | null>;
      })
      .catch(() => {
        clearTimeout(timeoutId);
        return null;
      });

    return from(fetchPromise).pipe(
      tap((response) => {
        if (response?.version && this.hasMinorOrMajorUpdate(response.version)) {
          this.latestRemoteVersion = response.version;
          this.updateReleaseMessage(response.message);
          if (!this.isDismissCooldownActive()) {
            this.ngZone.run(() => this.updateAvailableSub.next(true));
          }
        } else {
          this.ngZone.run(() => this.updateAvailableSub.next(false));
        }
      }),
      catchError(() => EMPTY)
    );
  }

  private hasMinorOrMajorUpdate(remoteVersion: string): boolean {
    const local = this.parseVersion(this.currentVersion);
    const remote = this.parseVersion(remoteVersion);

    if (!local || !remote) {
      return false;
    }

    if (remote.major > local.major) return true;
    if (remote.major === local.major && remote.minor > local.minor) return true;
    return false;
  }

  private parseVersion(version: string): { major: number; minor: number; patch: number } | null {
    const match = version.match(/^(\d+)\.(\d+)\.(\d+)$/);
    if (!match) return null;
    return {
      major: parseInt(match[1], 10),
      minor: parseInt(match[2], 10),
      patch: parseInt(match[3], 10),
    };
  }

  private updateReleaseMessage(message?: Record<string, string>): void {
    if (!message) {
      this.releaseMessageSub.next('');
      return;
    }
    const lang = this.translateService.currentLang || 'pt';
    const text = message[lang] || message['pt'] || message['en'] || '';
    this.releaseMessageSub.next(text);
  }

  private isDismissCooldownActive(): boolean {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!dismissedAt) return false;
    const elapsed = Date.now() - parseInt(dismissedAt, 10);
    return elapsed < DISMISS_COOLDOWN_MS;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
